import type { MarkerIcon } from "./markers.ts";

/**
 * What a player is doing this life.
 *
 * These are the game's own six roles, not an invented set. WARDOGS has no
 * fixed classes — you buy what you want — but progression runs across six
 * tracks, and the in-game map ships a marker icon for each of them. Using the
 * same six names and the same six icons means a commander reading this screen
 * and a player reading the in-game map are speaking one language.
 *
 * Command is deliberately absent: it is not something you pick, it is
 * something the session grants you. See `src/session/command.ts`.
 */

export type RoleId = "assault" | "driver" | "medic" | "pilot" | "recon" | "support";

export interface Role {
  readonly id: RoleId;
  readonly label: string;
  /** Short form for tight spaces like the order of battle. */
  readonly short: string;
  /** One line describing what this role contributes, shown when picking. */
  readonly blurb: string;
  /** Marker icon, matching the game's own UI asset. */
  readonly icon: MarkerIcon;
}

export const ROLES: readonly Role[] = [
  {
    id: "assault",
    label: "Assault",
    short: "ASL",
    blurb: "Takes and holds ground.",
    icon: "assault",
  },
  {
    id: "driver",
    label: "Driver",
    short: "DRV",
    blurb: "Ground vehicles, logistics and supply runs.",
    icon: "driver",
  },
  {
    id: "medic",
    label: "Medic",
    short: "MED",
    blurb: "Revives and keeps the squad in the fight.",
    icon: "medic",
  },
  {
    id: "pilot",
    label: "Pilot",
    short: "AIR",
    blurb: "Moves people and cargo by air.",
    icon: "pilot",
  },
  {
    id: "recon",
    label: "Recon",
    short: "RCN",
    blurb: "Spots for the team and feeds the map.",
    icon: "recon",
  },
  {
    id: "support",
    label: "Support",
    short: "SUP",
    blurb: "Builds, fortifies and supplies.",
    icon: "support",
  },
];

export const DEFAULT_ROLE: RoleId = "assault";

const ROLES_BY_ID = new Map<RoleId, Role>(ROLES.map((role) => [role.id, role]));

export function getRole(id: RoleId): Role {
  const role = ROLES_BY_ID.get(id);
  if (!role) throw new Error(`Unknown role: ${id}`);
  return role;
}

export function isRoleId(value: string): value is RoleId {
  return ROLES_BY_ID.has(value as RoleId);
}
